import { Dialog } from "@headlessui/react"
import { FC } from "react"
import { X } from "heroicons-react"
import { KButton, KSpan, KTitleSpan } from "kahuna-base-react-components"
import { tableCellClasses } from "../../../../constants"
import { lang } from "../../../../constants/languages"
import Loading from "../../../ui/Loading"
import { getCurrency } from "../../../../utility"

type ReceiptModalProps = {
  open: boolean
  onClose: () => void
  data: any
  loading: boolean
}

const formatDate = (dateStr: string) => {
  if (!dateStr) return "-"
  const d = new Date(dateStr)
  return d.toLocaleDateString("en-GB", { year: "numeric", month: "2-digit", day: "2-digit" })
}

const getPrimitiveValue = (value: any): string => {
  if (value === null || value === undefined || value === "") return "-"
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return String(value)
  }

  if (typeof value === "object") {
    if ("value" in value && value.value !== undefined && value.value !== null) {
      return String(value.value)
    }
    if ("amount" in value && value.amount !== undefined && value.amount !== null) {
      return String(value.amount)
    }
    if ("currency" in value && value.currency) {
      return String(value.currency)
    }
    if ("id" in value && value.id) {
      return String(value.id)
    }
    if ("reference" in value && value.reference) {
      return String(value.reference)
    }
    if ("text" in value && value.text) {
      return String(value.text)
    }
  }

  return "-"
}

const getReceiptFields = (data: any) => {
  const amount = getPrimitiveValue(data?.legs?.[0]?.amount ?? data?.amount)
  const currency = getPrimitiveValue(data?.legs?.[0]?.currency ?? data?.currency)
  const state = getPrimitiveValue(data?.state ?? data?.status).toLowerCase()
  const createdAt = formatDate(data?.createdAt ?? data?.created_at)
  const completedAt = formatDate(data?.completedAt ?? data?.completed_at)
  const reference = getPrimitiveValue(data?.reference)
  const transactionId = getPrimitiveValue(data?.id ?? data?.transaction_id)

  return {
    amount,
    currency,
    state,
    createdAt,
    completedAt,
    reference,
    transactionId
  }
}

const stateColors: Record<string, string> = {
  completed: "#22C55E",
  pending: "#F59E0B",
  failed: "#EF4444",
  reverted: "#EF4444"
}

const ReceiptModal: FC<ReceiptModalProps> = ({ open, onClose, data, loading }) => {
  const receiptLang = lang.royalties.statements.receipt_modal
  const getStateLabel = (state: string) => receiptLang.states[state as keyof typeof receiptLang.states] || state || "-"

  const handleDownloadPdf = () => {
    if (!data) return

    const { amount, currency, state, createdAt, completedAt, reference, transactionId } = getReceiptFields(data)

    const html = `
      <html>
      <head>
        <title>${receiptLang.title}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 40px; color: #111; }
          .header { text-align: center; margin-bottom: 40px; }
          .header h1 { font-size: 24px; margin-bottom: 4px; }
          .header p { color: #666; font-size: 14px; }
          .logo { width: 48px; height: 48px; margin-bottom: 16px; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
          td { padding: 12px 16px; border-bottom: 1px solid #eee; font-size: 14px; }
          td:first-child { color: #666; width: 40%; }
          td:last-child { font-weight: 500; }
          .amount { font-size: 28px; font-weight: 700; text-align: center; margin: 30px 0; }
          .status { display: inline-block; padding: 4px 12px; border-radius: 12px; font-size: 12px; font-weight: 600; text-transform: uppercase; }
          .footer { margin-top: 40px; text-align: center; color: #999; font-size: 12px; }
        </style>
      </head>
      <body>
        <div class="header">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" fill="none" class="logo">
            <path d="M36.2 8H18v10.5h17.8c3.5 0 6.3 2.2 6.3 5.5 0 3.3-2.8 5.5-6.3 5.5H18V36l19 22h12.6L30.4 35.5C37.8 34.6 44 29.5 44 23.5 44 15.2 38.8 8 36.2 8z" fill="#191C1F"/>
          </svg>
          <h1>${receiptLang.title}</h1>
          <p>${receiptLang.provider}</p>
        </div>
        <div class="amount">${amount} ${getCurrency(currency)}</div>
        <table>
          <tr><td>${receiptLang.status}</td><td>${getStateLabel(state)}</td></tr>
          <tr><td>${receiptLang.transaction_id}</td><td>${transactionId}</td></tr>
          <tr><td>${receiptLang.reference}</td><td>${reference}</td></tr>
          <tr><td>${receiptLang.created}</td><td>${createdAt}</td></tr>
          <tr><td>${receiptLang.completed}</td><td>${completedAt}</td></tr>
        </table>
        <div class="footer">
          <p>${receiptLang.generated_by}</p>
        </div>
      </body>
      </html>
    `

    const printWindow = window.open("", "_blank")
    if (printWindow) {
      printWindow.document.write(html)
      printWindow.document.close()
      printWindow.print()
    }
  }

  const renderRow = (label: string, value: string) => (
    <div className="flex justify-between py-3 gap-4" style={{ borderBottom: "1px solid #f0f0f0" }}>
      <KSpan text={label} color="#666" fontSize={14} />
      <KSpan text={value} color="#111" fontWeight={500} fontSize={14} />
    </div>
  )

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex justify-center items-center py-12">
          <Loading />
        </div>
      )
    }

    if (!data) {
      return (
        <div className="px-6 py-8 text-center">
          <KSpan text={receiptLang.data_not_available} color="#666" />
        </div>
      )
    }

    const { amount, currency, state, createdAt, completedAt, reference, transactionId } = getReceiptFields(data)

    return (
      <div>
        <div className="px-6 pt-6 pb-4">
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center mx-auto mb-3">
              <img src="/payment_icons/revolut.svg" alt="Revolut" style={{ width: 48, height: 48 }} />
            </div>
            <div className="text-center text-2xl font-bold mt-2">
              {amount} {getCurrency(currency)}
            </div>
            <div className="flex justify-center mt-2">
              <span
                className="text-xs font-semibold uppercase px-3 py-1 rounded-full"
                style={{
                  color: stateColors[state] || "#666",
                  background: `${stateColors[state] || "#666"}15`
                }}
              >
                {getStateLabel(state)}
              </span>
            </div>
          </div>
          <div className="mt-4">
            {renderRow(receiptLang.transaction_id, transactionId)}
            {renderRow(receiptLang.reference, reference)}
            {renderRow(receiptLang.created, createdAt)}
            {renderRow(receiptLang.completed, completedAt)}
          </div>
        </div>
        <div className="flex gap-4 p-4 px-6">
          <KButton
            text={receiptLang.close}
            background="#F7F7F7"
            textColor="#000"
            height="48px"
            padding="14px"
            onClick={onClose}
          />
          <KButton
            text={receiptLang.download_pdf}
            background="#000"
            textColor="#FFF"
            height="48px"
            padding="14px"
            onClick={handleDownloadPdf}
          />
        </div>
      </div>
    )
  }

  return (
    <Dialog open={open} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-[rgba(0,0,0,0.3)]" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg" style={{ minWidth: 420, maxWidth: 500 }}>
          <div
            className={`bg-gray-50 text-xs text-gray-500 font-medium rounded-t-lg
              uppercase border-b flex justify-between ${tableCellClasses.sm}`}
          >
            <KTitleSpan
              text={receiptLang.title}
              color="#111"
              fontWeight={500}
              fontSize={14}
              lineHeight="20px"
              letterSpacing="0.2px"
            />
            <X className="h-4 mr-[-6px] cursor-pointer" onClick={onClose} />
          </div>
          {renderContent()}
        </div>
      </div>
    </Dialog>
  )
}

export default ReceiptModal
